// 前端错误监控 — 全局 JS 错误捕获 + 上报
// 覆盖：window.onerror / unhandledrejection / React ErrorBoundary / 手动上报

export interface ErrorReport {
  type: "js_error" | "unhandled_rejection" | "react_error_boundary" | "manual" | "api_error";
  message: string;
  stack?: string;
  componentStack?: string;
  url?: string;
  userAgent?: string;
  timestamp?: number;
  extra?: Record<string, unknown>;
}

const MAX_QUEUE = 50;
const reportQueue: ErrorReport[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * 上报错误到后端（批量 + 防抖）
 */
export function reportError(report: ErrorReport): void {
  reportQueue.push({ ...report, timestamp: Date.now() });
  if (reportQueue.length >= MAX_QUEUE) {
    flushReports();
  } else {
    scheduleFlush();
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(flushReports, 3000);
}

async function flushReports(): Promise<void> {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (reportQueue.length === 0) return;

  const batch = reportQueue.splice(0, MAX_QUEUE);
  try {
    const token = localStorage.getItem("accessToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";
    await fetch(`${BASE_URL}/admin/client-errors`, {
      method: "POST",
      headers,
      body: JSON.stringify({ errors: batch }),
      keepalive: true, // 页面关闭时也能发送
    });
  } catch {
    // 上报失败不阻塞，静默丢弃（避免无限循环）
    console.warn("[errorMonitor] 上报失败，已丢弃", batch.length, "条错误");
  }
}

/**
 * 初始化全局错误监听
 */
export function initErrorMonitor(): void {
  // 1. 全局 JS 错误
  window.addEventListener("error", (event) => {
    // 忽略资源加载错误（图片/脚本 404 等）
    if (event.target && event.target !== window) return;
    reportError({
      type: "js_error",
      message: event.message || "Unknown error",
      stack: event.error?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  });

  // 2. 未处理的 Promise 拒绝
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    reportError({
      type: "unhandled_rejection",
      message: reason?.message || String(reason) || "Unhandled Promise rejection",
      stack: reason?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  });

  // 3. 页面关闭前刷新上报
  window.addEventListener("beforeunload", () => {
    if (reportQueue.length > 0) {
      flushReports();
    }
  });

  // 4. 控制台覆盖：开发环境保留原始行为，生产环境额外上报
  if (import.meta.env.PROD) {
    const originalError = console.error;
    console.error = (...args: unknown[]) => {
      originalError.apply(console, args);
      // 避免无限循环：只上报非 errorMonitor 的错误
      const msg = args.map((a) => typeof a === "string" ? a : JSON.stringify(a)).join(" ");
      if (!msg.includes("[errorMonitor]") && !msg.includes("[ErrorBoundary]")) {
        reportError({
          type: "manual",
          message: msg.slice(0, 500),
          url: window.location.href,
        });
      }
    };
  }

  console.log("[errorMonitor] 全局错误监控已启动");
}

/**
 * 手动上报（用于业务逻辑中的异常捕获）
 */
export function captureException(error: Error | string, extra?: Record<string, unknown>): void {
  reportError({
    type: "manual",
    message: typeof error === "string" ? error : error.message,
    stack: typeof error === "string" ? undefined : error.stack,
    extra,
    url: window.location.href,
    userAgent: navigator.userAgent,
  });
}
